<div class="lookup-page">
    <header class="lookup-header">
        <div>
            <h1 class="lookup-title">Item lookup</h1>
            <p class="lookup-subtitle">Search an item, then choose past sales or open orders.</p>
        </div>
        <button
            pButton
            type="button"
            label="Back to Orders"
            icon="pi pi-arrow-left"
            class="p-button-outlined lookup-back"
            (click)="goToOrders()"
        ></button>
    </header>

    <div class="lookup-choices" role="radiogroup" aria-label="What to look up">
        <button
            type="button"
            class="lookup-choice"
            [class.is-active]="view === 'lastSold'"
            role="radio"
            [attr.aria-checked]="view === 'lastSold'"
            (click)="setView('lastSold')"
        >
            <span class="lookup-choice__icon" aria-hidden="true"><i class="pi pi-chart-bar"></i></span>
            <span class="lookup-choice__body">
                <span class="lookup-choice__title">Past sales</span>
                <span class="lookup-choice__text">See which customers bought this item, at what price, and when.</span>
            </span>
        </button>
        <button
            type="button"
            class="lookup-choice"
            [class.is-active]="view === 'stock'"
            role="radio"
            [attr.aria-checked]="view === 'stock'"
            (click)="setView('stock')"
        >
            <span class="lookup-choice__icon" aria-hidden="true"><i class="pi pi-list"></i></span>
            <span class="lookup-choice__body">
                <span class="lookup-choice__title">Open orders</span>
                <span class="lookup-choice__text">See open orders that still hold this item, and the quantity on each. Status CRT, PKL, and WIP only.</span>
            </span>
        </button>
    </div>

    <div class="lookup-filters">
        <div class="lookup-field lookup-field--item">
            <span class="lookup-field__label">Item</span>
            <div class="lookup-item-control" [class.has-selection]="!!selectedItemNo">
                <i class="pi pi-search lookup-item-control__search" aria-hidden="true"></i>
                <ng-container *ngIf="selectedItemNo; else itemSearchInput">
                    <span class="lookup-item-code">{{ selectedItemNo }}</span>
                    <span class="lookup-item-sep" aria-hidden="true"></span>
                    <span class="lookup-item-desc">{{ selectedItemDescription }}</span>
                    <button
                        type="button"
                        class="lookup-item-clear"
                        aria-label="Clear item"
                        (click)="clearItem()"
                    >
                        <i class="pi pi-times" aria-hidden="true"></i>
                    </button>
                </ng-container>
                <ng-template #itemSearchInput>
                    <p-autoComplete
                        class="lookup-item-search"
                        [ngModel]="selectedSuggestion"
                        (ngModelChange)="onSearchModelChange($event)"
                        [suggestions]="suggestions"
                        [minLength]="3"
                        [delay]="400"
                        [forceSelection]="true"
                        [showClear]="true"
                        [dropdown]="true"
                        field="FINALRESPNSE"
                        placeholder="Search item number or description"
                        appendTo="body"
                        (completeMethod)="searchItems($event)"
                        (onSelect)="onItemSelected($event)"
                        (onClear)="clearItem()"
                    ></p-autoComplete>
                </ng-template>
            </div>
            <p class="lookup-field__hint">Search an item by code or description. Then view past sales or open orders.</p>
        </div>

        <label class="lookup-field" *ngIf="view === 'lastSold'">
            <span class="lookup-field__label">How many past sales</span>
            <select
                class="lookup-select"
                [(ngModel)]="maxRecords"
                (ngModelChange)="onMaxRecordsChange()"
                aria-label="How many sales to load"
            >
                <option *ngFor="let option of maxRecordOptions" [ngValue]="option.value">{{ option.label }}</option>
            </select>
        </label>

        <button
            pButton
            type="button"
            label="Search"
            icon="pi pi-search"
            class="lookup-search-btn"
            [disabled]="!canSearch"
            [loading]="loading"
            (click)="search()"
        ></button>
    </div>

    <p *ngIf="searchHint && !errorMessage && !selectedItemNo" class="lookup-message">{{ searchHint }}</p>
    <p *ngIf="errorMessage" class="lookup-message is-error">{{ errorMessage }}</p>
    <p *ngIf="!errorMessage && searched && !loading && view === 'lastSold' && rows.length === 0" class="lookup-message">
        No past sales were found for this item.
    </p>
    <p *ngIf="!errorMessage && searched && !loading && view === 'stock' && stockRows.length === 0" class="lookup-message">
        No open orders found for this item (CRT, PKL, WIP).
    </p>

    <div *ngIf="view === 'lastSold' && rows.length > 0" class="lookup-results">
        <div class="lookup-results__head">
            <div class="lookup-results__title-row">
                <h2 class="lookup-results__title">Past sales</h2>
                <span class="lookup-results__badge">{{ recordLabel }}</span>
            </div>
            <span class="lookup-results__item-meta" *ngIf="selectedItemMeta">Item: {{ selectedItemMeta }}</span>
        </div>
        <p-table
            [value]="rows"
            [paginator]="true"
            [rows]="pageSize"
            [showCurrentPageReport]="true"
            currentPageReportTemplate="Showing {totalRecords} of {totalRecords} sales"
            [rowsPerPageOptions]="[]"
            [scrollable]="true"
            scrollHeight="520px"
            styleClass="lookup-table"
            [stripedRows]="true"
        >
            <ng-template pTemplate="header">
                <tr>
                    <th>Customer</th>
                    <th>Order</th>
                    <th>Item description</th>
                    <th class="num">Qty sold</th>
                    <th class="num">Sales price</th>
                    <th class="num">Date</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
                <tr>
                    <td>{{ customerLabel(row) }}</td>
                    <td>{{ row.orderNumber }}</td>
                    <td class="desc">{{ row.description }}</td>
                    <td class="num">{{ formatQty(row.quantity) }}</td>
                    <td class="num"><span class="lookup-price">{{ formatPrice(row.salesPrice) }}</span></td>
                    <td class="num">{{ formatDate(row.orderDate) }}</td>
                </tr>
            </ng-template>
        </p-table>
    </div>

    <div *ngIf="view === 'stock' && stockRows.length > 0" class="lookup-results">
        <div class="lookup-results__head">
            <div class="lookup-results__title-row">
                <h2 class="lookup-results__title">Open orders</h2>
                <span class="lookup-results__badge">{{ recordLabel }}</span>
            </div>
            <span class="lookup-results__item-meta" *ngIf="selectedItemMeta">Item: {{ selectedItemMeta }}</span>
        </div>
        <p-table
            [value]="stockRows"
            [paginator]="true"
            [rows]="pageSize"
            [showCurrentPageReport]="true"
            currentPageReportTemplate="Showing {totalRecords} of {totalRecords} order lines"
            [rowsPerPageOptions]="[]"
            [scrollable]="true"
            scrollHeight="520px"
            styleClass="lookup-table"
            [stripedRows]="true"
        >
            <ng-template pTemplate="header">
                <tr>
                    <th>Order</th>
                    <th>Status</th>
                    <th>Customer</th>
                    <th class="num">Qty on order</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
                <tr>
                    <td>{{ row.orderNumber }}</td>
                    <td><span class="lookup-status">{{ statusLabel(row.orderMovingStatus) }}</span></td>
                    <td>{{ customerLabel(row) }}</td>
                    <td class="num">{{ formatQty(row.qty) }}</td>
                </tr>
            </ng-template>
        </p-table>
    </div>
</div>
